import { useMemo } from 'react';
import { useAppState } from '@/ui/AppState';
import { navigate } from '@/ui/router';
import { Banner, EmptyState } from '@/ui/components/bits';
import { buildCheatSheet } from '@/domain/reference';

/**
 * Built automatically from the reference sections of every imported session.
 * Printing goes through the browser's own dialog, so "Save as PDF" is whatever
 * the device already offers.
 */
export function CheatSheetScreen() {
  const { formulas, constants, sessions } = useAppState();

  const sheet = useMemo(
    () => buildCheatSheet(formulas, constants, sessions),
    [formulas, constants, sessions],
  );

  if (sheet.formulaCount === 0 && sheet.constantCount === 0) {
    return (
      <div className="stack">
        <h1>Cheat sheet</h1>
        <EmptyState icon="∑" title="No formulas or constants yet">
          This sheet builds itself from the <code>reference</code> section of your import blocks.
          None of your classes have one yet — ask the chat to include formulas and constants next
          time and they will appear here automatically.
        </EmptyState>
        <button className="btn" onClick={() => navigate('/add')}>
          Add a class
        </button>
      </div>
    );
  }

  return (
    <div className="stack sheet">
      <div className="spread print-hide">
        <h1>Cheat sheet</h1>
        <button className="btn btn--primary btn--sm" onClick={() => window.print()}>
          Print / Save as PDF
        </button>
      </div>

      {sheet.issues.length > 0 ? (
        <div className="print-hide stack">
          {sheet.issues.map((issue) => (
            <Banner key={issue.kind} kind={issue.kind === 'odd-constant' ? 'warn' : 'info'}>
              {issue.message}
            </Banner>
          ))}
        </div>
      ) : null}

      <header className="sheet__head">
        <h2 className="sheet__title">Formulas and constants</h2>
        <div className="sheet__meta mono">
          CHE1045 · {sheet.formulaCount} formula{sheet.formulaCount === 1 ? '' : 's'} ·{' '}
          {sheet.constantCount} constant{sheet.constantCount === 1 ? '' : 's'} ·{' '}
          {new Date().toLocaleDateString()}
        </div>
      </header>

      {sheet.groups.map((group) => (
        <section key={group.topic} className="sheet__group card">
          <h3 className="sheet__topic section-title" style={{ marginTop: 0 }}>
            {group.topic}
          </h3>
          <div className="stack" style={{ gap: 'var(--sp-2)' }}>
            {group.formulas.map((formula) => (
              <div key={formula.id} className="sheet__row">
                <div className="sheet__name">{formula.name}</div>
                <div className="sheet__expr mono">{formula.expr}</div>
                {formula.notes ? <div className="sheet__notes faint">{formula.notes}</div> : null}
              </div>
            ))}
          </div>
        </section>
      ))}

      {sheet.constants.length > 0 ? (
        <section className="sheet__group card">
          <h3 className="sheet__topic section-title" style={{ marginTop: 0 }}>
            Constants
          </h3>
          <div className="stack" style={{ gap: 'var(--sp-2)' }}>
            {sheet.constants.map((constant) => (
              <div key={constant.id} className="sheet__row">
                <div className="sheet__name">
                  {constant.name} <span className="mono faint">{constant.symbol}</span>
                </div>
                <div className="sheet__expr mono">
                  {constant.value}
                  {constant.units ? ` ${constant.units}` : ''}
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
